import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, Loader2, Upload } from 'lucide-react';
import {
  LEGAL_CATEGORY_LABELS,
  readMarkdownFileAsText,
  fetchLegalPolicyVersionById,
  updateLegalPolicyDraft,
  type LegalPolicyVersionDto,
} from '../../services/MasterAdmin/legalPolicyVersionsService';

const LegalPolicyEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [row, setRow] = useState<LegalPolicyVersionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [markdown, setMarkdown] = useState('');
  const [markdownImportLabel, setMarkdownImportLabel] = useState<string | null>(null);
  const markdownFileInputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) {
      setLoadError('Missing policy id.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetchLegalPolicyVersionById(id);
      setRow(res.data);
      setTitle(res.data.title || '');
      setMarkdown(res.data.markdownContent);
      setMarkdownImportLabel(null);
    } catch (e: unknown) {
      const msg =
        typeof e === 'object' &&
        e !== null &&
        'response' in e &&
        typeof (e as { response?: { data?: { message?: string } } }).response?.data?.message === 'string'
          ? String((e as { response?: { data?: { message?: string } } }).response?.data?.message)
          : 'Failed to load policy version.';
      setLoadError(msg);
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!row || !id) return;
    setSaveError(null);
    if (!markdown.trim()) {
      setSaveError('Enter markdown content or upload a .md file.');
      return;
    }
    setBusy(true);
    try {
      await updateLegalPolicyDraft(id, { markdownContent: markdown, title }, null);
      navigate('/operations/legal-policies');
    } catch (e: unknown) {
      const msg =
        typeof e === 'object' &&
        e !== null &&
        'response' in e &&
        typeof (e as { response?: { data?: { message?: string } } }).response?.data?.message === 'string'
          ? String((e as { response?: { data?: { message?: string } } }).response?.data?.message)
          : 'Failed to save.';
      setSaveError(msg);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center p-8 text-gray-500 dark:text-gray-400">
        <Loader2 className="h-10 w-10 animate-spin" />
      </div>
    );
  }

  if (loadError || !row) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 p-3 sm:p-6">
        <Link
          to="/operations/legal-policies"
          className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to list
        </Link>
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
          {loadError || 'Not found.'}
        </div>
      </div>
    );
  }

  if (row.isPublished) {
    return (
      <div className="mx-auto max-w-3xl space-y-6 p-3 sm:p-6">
        <Link
          to="/operations/legal-policies"
          className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to list
        </Link>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 dark:border-amber-900/40 dark:bg-amber-900/15">
          <h1 className="text-lg font-bold text-gray-900 dark:text-white">Cannot edit published version</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            {LEGAL_CATEGORY_LABELS[row.category]} · v{row.version} · {row.audience} is live. Create a new version from
            the list, or unpublish first to edit this copy.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 max-w-3xl mx-auto space-y-6 p-3 sm:p-4 md:p-6">
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to="/operations/legal-policies"
          className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to list
        </Link>
      </div>

      <div className="flex items-start gap-3">
        <FileText className="mt-1 h-6 w-6 shrink-0 text-blue-600 dark:text-blue-400" />
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Edit draft</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            {LEGAL_CATEGORY_LABELS[row.category]} · v{row.version} · {row.audience}
          </p>
        </div>
      </div>

      <form
        onSubmit={(e) => void handleSave(e)}
        className="space-y-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 md:p-6"
      >
        {saveError && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
            {saveError}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Title</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-950 dark:text-white"
            placeholder="Title"
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Markdown body</label>
          <div className="mb-2 flex flex-wrap items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800">
              <Upload className="h-3.5 w-3.5" />
              Replace from .md
              <input
                ref={markdownFileInputRef}
                type="file"
                accept=".md,text/markdown,text/plain,.markdown"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (!f) return;
                  void readMarkdownFileAsText(f)
                    .then((text: string) => {
                      setMarkdown(text);
                      setMarkdownImportLabel(f.name);
                      setSaveError(null);
                    })
                    .catch(() => {
                      setSaveError('Could not read the file. Try a UTF-8 .md file.');
                    });
                }}
              />
            </label>
            {markdownImportLabel && (
              <button
                type="button"
                className="text-xs font-medium text-gray-600 underline-offset-2 hover:underline dark:text-gray-400"
                onClick={() => {
                  setMarkdownImportLabel(null);
                  if (markdownFileInputRef.current) markdownFileInputRef.current.value = '';
                }}
              >
                Clear import label
              </button>
            )}
          </div>
          {markdownImportLabel && (
            <p className="mb-2 text-xs text-gray-600 dark:text-gray-400">
              Replaced editor from <span className="font-medium">{markdownImportLabel}</span>. Edit below if needed.
            </p>
          )}
          <textarea
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            rows={18}
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 font-mono text-xs dark:border-gray-600 dark:bg-gray-950 dark:text-white"
          />
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-700">
          <Link
            to="/operations/legal-policies"
            className="inline-flex rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700/50"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={busy || !markdown.trim()}
            className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save draft'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LegalPolicyEditPage;
